/* ============================================================
   Lembretes de refeição

   Todo aviso que existia até aqui nasce de um evento DENTRO de um
   desafio: alguém postou, alguém comentou, alguém deu nota. Por isso
   eles moram em `challenges/` e são disparados por gatilho.

   O lembrete é o contrário: ele não é evento de grupo, é evento da
   PESSOA. "É meio-dia, registra o almoço" não depende de ninguém ter
   feito nada, e não muda de sentido se a pessoa está em um desafio ou em
   dez.

   Essa diferença é o que decide onde ele mora. Se o lembrete fosse
   configurado por desafio, quem está em dez grupos levaria dez avisos ao
   meio-dia — e nenhuma deduplicação depois consertaria isso direito,
   porque cada grupo teria a sua hora e o seu texto. Morando em
   `users/{uid}` e sendo disparado por uma função AGENDADA que varre
   pessoas (nunca grupos), a unicidade é estrutural: existe um slot de
   almoço por pessoa, então só pode sair um aviso.

   Este módulo é o vocabulário comum. O app precisa dele para desenhar a
   tela e para calcular a chave de roteamento; a função precisa dele para
   saber que horas são na vida de cada pessoa. Duas cópias divergiriam e
   a pessoa receberia o lembrete numa hora que a tela não mostra.
   ============================================================ */

import type { DayKey } from "./types.js";

/** Os ids são os mesmos de `DEFAULT_MEALS`, e isso não é coincidência:
    é o que permite conferir se a refeição JÁ foi registrada hoje. */
export type LembreteId = "cafe" | "almoco" | "lanche" | "janta";

export interface Lembrete {
  id: LembreteId;
  ligado: boolean;
  /** "HH:MM" no fuso de quem escolheu. */
  hora: string;
  /** Mensagem própria. Vazio = usa a padrão do slot. */
  texto: string;
}

/**
 * A última chamada do dia.
 *
 * Não é uma refeição, e por isso não é um slot: ela não pergunta se você
 * almoçou, pergunta se o DIA vai fechar vazio. Entre a janta e a
 * meia-noite não existia nada, e quem tinha seis dias seguidos perdia a
 * sequência sem nenhum aviso — que é a única perda concreta que este app
 * sabe cobrar.
 *
 * Mora aqui dentro, e não num documento próprio, porque ela usa a mesma
 * chave de roteamento (`lembretesUTC`) e a mesma varredura de cinco em
 * cinco minutos. Um segundo agendador para o mesmo trabalho seria um
 * segundo lugar para o fuso horário errar.
 */
export interface UltimaChamada {
  ligado: boolean;
  /** "HH:MM" no mesmo fuso dos outros lembretes. */
  hora: string;
}

export interface Lembretes {
  /** Fuso IANA do aparelho que salvou por último. Ver `horaLocal`. */
  fuso: string;
  /** Não avisar se a refeição já foi registrada hoje. */
  soSeFaltar: boolean;
  slots: Lembrete[];
  ultimaChamada: UltimaChamada;
}

/**
 * De quantos dias seguidos a última chamada passa a valer a pena.
 *
 * Abaixo disso não há o que perder, e um aviso que diz "sua sequência de
 * 1 dia acaba hoje" é uma cobrança sobre nada — o jeito mais rápido de
 * ensinar a pessoa a ignorar este aviso justamente nos dias em que ele
 * importa.
 */
export const SEQUENCIA_MINIMA_PRA_AVISAR = 3;

/** O horário que vem preenchido antes de alguém escolher. */
export const HORA_DA_ULTIMA_CHAMADA = "21:00";

export const ULTIMA_CHAMADA_PADRAO: UltimaChamada = {
  ligado: false, hora: HORA_DA_ULTIMA_CHAMADA,
};

export const FUSO_PADRAO = "America/Sao_Paulo";

/**
 * De quantos em quantos minutos a varredura acorda — e quantos minutos
 * cada uma delas cobre.
 *
 * Os dois são o MESMO número, e é isso que faz o horário escolhido não
 * precisar ser arredondado. A primeira versão disto tinha uma grade de
 * 15 minutos: quem escolhia 19:55 via a tela responder 19:45, calado, e
 * a leitura óbvia era "a edição não pegou". Comida não acontece em grade
 * de quarto de hora, e mexer no valor que a pessoa acabou de escolher é
 * a pior coisa que um campo pode fazer.
 *
 * Então a escolha é livre, ao minuto, e quem se ajusta é a varredura:
 * cada execução cobre a JANELA dos cinco minutos que acabaram de passar,
 * em vez de casar com um instante exato. Duas consequências, as duas
 * boas:
 *
 * - o lembrete pode chegar até cinco minutos depois da hora marcada, o
 *   que em lembrete de refeição não se percebe;
 * - execução atrasada não perde ninguém. A janela é ancorada no RELÓGIO,
 *   não no instante em que a função acordou, então um disparo que chega
 *   três minutos tarde ainda cobre exatamente os mesmos cinco minutos.
 *   Com casamento exato, um segundo de atraso já pulava a pessoa.
 */
export const PASSO_DA_VARREDURA_MIN = 5;

export interface ModeloDeLembrete {
  id: LembreteId;
  label: string;
  emoji: string;
  /** O horário que aparece já preenchido antes de alguém escolher. */
  hora: string;
  titulo: string;
  corpo: string;
}

/**
 * Os quatro slots, fechados de propósito.
 *
 * Deixar a pessoa criar slots à vontade transformaria o lembrete num
 * agendador genérico — e um app que manda quantas notificações a pessoa
 * quiser é um app que a loja olha com desconfiança. Quatro cobrem o dia
 * de quem registra comida; quem quiser outro horário move um destes.
 */
export const MODELO_LEMBRETES: readonly ModeloDeLembrete[] = [
  {
    id: "cafe", label: "Café da manhã", emoji: "☕", hora: "08:00",
    titulo: "Café da manhã",
    corpo: "Registra o café antes que o dia comece a correr.",
  },
  {
    id: "almoco", label: "Almoço", emoji: "🍽️", hora: "12:00",
    titulo: "Hora do almoço",
    corpo: "Tira a foto do prato antes da primeira garfada.",
  },
  {
    id: "lanche", label: "Lanche", emoji: "🥪", hora: "16:00",
    titulo: "Lanche da tarde",
    corpo: "Lanche também é prato, e também vale ponto.",
  },
  {
    id: "janta", label: "Janta", emoji: "🍝", hora: "19:30",
    titulo: "Hora da janta",
    corpo: "Fecha o dia com a foto da janta.",
  },
];

export const acharModelo = (id: string): ModeloDeLembrete | undefined =>
  MODELO_LEMBRETES.find((m) => m.id === id);

/**
 * Todos desligados, e isso é escolha, não descuido.
 *
 * Ligar três avisos diários em quem nunca pediu é o caminho mais curto
 * para a pessoa desligar as notificações NO SISTEMA — e aí vão junto o
 * comentário e a nota, que ela queria receber. Os horários já vêm
 * preenchidos para que ligar seja um toque, não um formulário.
 */
export const LEMBRETES_PADRAO: Lembretes = {
  fuso: FUSO_PADRAO,
  soSeFaltar: true,
  slots: MODELO_LEMBRETES.map((m) => ({
    id: m.id, ligado: false, hora: m.hora, texto: "",
  })),
  ultimaChamada: ULTIMA_CHAMADA_PADRAO,
};

/* ---------- horas ---------- */

const dois = (n: number): string => String(n).padStart(2, "0");

/** "7:5" -> [7, 5]; lixo -> null. */
function partirHora(hora: unknown): [number, number] | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hora ?? "").trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (!Number.isInteger(h) || !Number.isInteger(min)) return null;
  if (h < 0 || h > 23 || min < 0 || min > 59) return null;
  return [h, min];
}

/**
 * Põe a hora no formato "HH:MM" — e NÃO mexe no minuto escolhido.
 *
 * Só normaliza (zero à esquerda) e recusa o impossível. Nenhum
 * arredondamento: o valor que volta para a tela é o que a pessoa
 * escolheu, sempre. Hora que não dá para entender vira meio-dia, porque
 * um campo vazio faria o slot nunca casar com minuto nenhum e o lembrete
 * sumiria sem erro aparecer em lugar nenhum.
 */
export function normalizarHora(hora: unknown): string {
  const p = partirHora(hora);
  if (!p) return "12:00";
  return `${dois(p[0])}:${dois(p[1])}`;
}

/**
 * Que horas são, agora, na vida de quem escolheu o horário.
 *
 * O servidor não faz conta de fuso: pede ao `Intl` a hora naquele fuso e
 * compara com o texto que a pessoa salvou. É o que faz horário de verão
 * de qualquer país funcionar sem nenhuma tabela aqui dentro.
 *
 * `hourCycle: "h23"` explícito porque `hour12: false` devolve "24:00" à
 * meia-noite em parte das implementações — e "24:00" nunca casaria com
 * um horário escolhido na tela.
 */
export function horaLocal(quando: Date, fuso: string): string {
  try {
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: fuso, hour: "2-digit", minute: "2-digit", hourCycle: "h23",
    }).format(quando);
  } catch {
    // Fuso com lixo: cai no de casa em vez de derrubar a varredura
    // inteira por causa de um documento.
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: FUSO_PADRAO, hour: "2-digit", minute: "2-digit", hourCycle: "h23",
    }).format(quando);
  }
}

/** O dia como quem postou conta — o mesmo `day` que vai no prato. */
export function diaLocal(quando: Date, fuso: string): DayKey {
  const monta = (tz: string) =>
    new Intl.DateTimeFormat("en-CA", {
      timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit",
    }).format(quando);
  try {
    return monta(fuso);
  } catch {
    return monta(FUSO_PADRAO);
  }
}

/** O minuto exato deste instante, em UTC. */
export const minutoUTC = (quando: Date): string =>
  `${dois(quando.getUTCHours())}:${dois(quando.getUTCMinutes())}`;

/**
 * Os minutos que ESTA execução da varredura cobre, do mais velho ao mais
 * novo.
 *
 * Ancorada no relógio e não em "agora menos cinco minutos": o fim da
 * janela é o instante atual arredondado para baixo na grade do passo.
 * É o que faz duas execuções seguidas cobrirem faixas coladas, sem
 * buraco e sem sobreposição — e o que faz uma execução atrasada cobrir a
 * mesma faixa que cobriria se tivesse saído na hora.
 */
export function janelaDaVarredura(
  agora: Date, passo: number = PASSO_DA_VARREDURA_MIN,
): Date[] {
  const fim = new Date(agora.getTime());
  fim.setUTCSeconds(0, 0);
  fim.setUTCMinutes(Math.floor(fim.getUTCMinutes() / passo) * passo);

  const minutos: Date[] = [];
  for (let i = passo - 1; i >= 0; i--) minutos.push(new Date(fim.getTime() - i * 60_000));
  return minutos;
}

/**
 * Os horários ligados, convertidos para UTC — a CHAVE DE ROTEAMENTO.
 *
 * Sem ela a varredura teria que ler a base de usuários inteira a cada
 * cinco minutos só para descobrir quem tem lembrete agora. Com ela, é um
 * `array-contains-any` sobre os minutos da janela.
 *
 * Quem calcula é o APP, porque só ele sabe o fuso do aparelho. Isso não
 * abre brecha: mentir aqui só adianta ou atrasa o próprio lembrete, e o
 * servidor confere o slot de novo a partir do `fuso` antes de mandar
 * qualquer coisa. A chave roteia; ela não autoriza.
 *
 * Usa o fuso do AMBIENTE em que roda (`setHours` é local), e por isso só
 * faz sentido chamada no app. Deslocamento que muda — viagem ou horário
 * de verão — deixa a chave velha até o app reabrir; ver
 * `app/src/lib/lembretes.ts`.
 */
export function minutosUTC(lembretes: Lembretes, agora: Date = new Date()): string[] {
  const minutos = new Set<string>();
  const horas = [
    ...lembretes.slots.filter((s) => s.ligado).map((s) => s.hora),
    /* A última chamada entra na MESMA chave. Uma chave separada seria um
       segundo campo para o servidor consultar, e a consulta é um
       `array-contains-any` — dois campos virariam duas varreduras. */
    ...(lembretes.ultimaChamada.ligado ? [lembretes.ultimaChamada.hora] : []),
  ];
  for (const hora of horas) {
    const p = partirHora(hora);
    if (!p) continue;
    const quando = new Date(agora.getTime());
    quando.setHours(p[0], p[1], 0, 0);
    minutos.add(minutoUTC(quando));
  }
  return [...minutos].sort();
}

/* ---------- leitura desconfiada ---------- */

/**
 * Lê o que está no banco sem acreditar em nada.
 *
 * Mesmo motivo de `resolverAppConfig`: o documento pode estar vazio,
 * vir de uma versão antiga do app ou ter lixo. Campo estragado vira
 * padrão em vez de virar `undefined` — um `hora` indefinido faria o slot
 * nunca casar com balde nenhum, e a pessoa ficaria sem lembrete sem
 * nenhum erro aparecer em lugar nenhum.
 */
export function resolverLembretes(bruto: unknown): Lembretes {
  const d = (bruto ?? {}) as Record<string, unknown>;
  const salvos = Array.isArray(d["slots"]) ? (d["slots"] as unknown[]) : [];

  const porId = new Map<string, Record<string, unknown>>();
  for (const s of salvos) {
    if (s && typeof s === "object") {
      const item = s as Record<string, unknown>;
      if (typeof item["id"] === "string") porId.set(item["id"], item);
    }
  }

  const fuso = typeof d["fuso"] === "string" && d["fuso"].trim()
    ? d["fuso"].trim()
    : FUSO_PADRAO;

  const bruta = (d["ultimaChamada"] ?? {}) as Record<string, unknown>;

  return {
    fuso,
    soSeFaltar: d["soSeFaltar"] !== false,
    ultimaChamada: {
      ligado: bruta["ligado"] === true,
      hora: normalizarHora(
        typeof bruta["hora"] === "string" ? bruta["hora"] : HORA_DA_ULTIMA_CHAMADA),
    },
    /* A lista sai do MODELO, não do banco: slot que a gente deixar de
       oferecer some sozinho, e slot novo nasce com o padrão sem nenhuma
       migração. */
    slots: MODELO_LEMBRETES.map((m) => {
      const salvo = porId.get(m.id) ?? {};
      return {
        id: m.id,
        ligado: salvo["ligado"] === true,
        hora: normalizarHora(typeof salvo["hora"] === "string" ? salvo["hora"] : m.hora),
        texto: typeof salvo["texto"] === "string" ? salvo["texto"].slice(0, 120).trim() : "",
      };
    }),
  };
}

/** Tem algum ligado? A tela usa para explicar por que nada chega. */
export const algumLembreteLigado = (l: Lembretes): boolean =>
  l.slots.some((s) => s.ligado) || l.ultimaChamada.ligado;

/**
 * O que a última chamada diz.
 *
 * O NÚMERO vai no texto porque é ele que faz a pessoa abrir o app: "sua
 * sequência acaba hoje" é um aviso, "seus 8 dias acabam hoje" é uma
 * perda com tamanho. É a mesma razão de o cartão de vale-falta dizer
 * quantos dias voltam em vez de "usar um vale-falta?".
 */
export function textoDaUltimaChamada(sequencia: number): { title: string; body: string } {
  const n = Math.trunc(Number(sequencia));
  const dias = `${n} ${n === 1 ? "dia" : "dias"}`;
  return {
    title: `🔥 Seus ${dias} acabam à meia-noite`,
    body: "Você ainda não postou hoje. Uma foto salva a sequência.",
  };
}

/**
 * Como o grupo está nesta refeição, hoje.
 *
 * `total` é quanta gente o desafio tem; `jaPostaram`, quantas já
 * registraram ESTA refeição. Quem chama sabe ler os pratos do dia; este
 * módulo só sabe virar isso em frase.
 */
export interface GrupoNaRefeicao {
  jaPostaram: number;
  total: number;
  euJaPostei: boolean;
}

/**
 * A frase que transforma alarme em cobrança.
 *
 * O lembrete sabia só uma coisa sobre o mundo: se VOCÊ já tinha
 * registrado. "Hora do almoço" é um despertador, e despertador se
 * desliga. "3 de 5 já postaram" é o grupo esperando — e é a mesma
 * informação que faz alguém abrir o app sem lembrete nenhum.
 *
 * Três recusas, todas pelo mesmo motivo (frase que não diz nada gasta a
 * paciência de quem lê):
 *
 * - desafio de uma pessoa só não tem grupo, e "0 de 1 já postaram" é uma
 *   cobrança de você para você;
 * - quem já postou não é cobrado. Ele só está aqui porque desligou o
 *   "não avisar se já registrei", e cobrar quem cumpriu é o caminho mais
 *   curto para o lembrete virar spam;
 * - número que não existe não vira frase: `total` ou `jaPostaram`
 *   estragados somem em vez de virar "NaN de 5".
 */
export function fraseDoGrupo(grupo: GrupoNaRefeicao | undefined): string {
  if (!grupo) return "";

  const total = Math.trunc(Number(grupo.total));
  const jaPostaram = Math.trunc(Number(grupo.jaPostaram));
  if (!Number.isFinite(total) || !Number.isFinite(jaPostaram)) return "";
  if (total < 2 || jaPostaram < 0 || jaPostaram > total) return "";
  if (grupo.euJaPostei) {
    return jaPostaram > 0 ? `${jaPostaram} de ${total} já postaram.` : "";
  }

  if (jaPostaram === 0) return "Ninguém postou ainda — seja o primeiro.";
  // O caso mais forte da lista, e o mais barato de checar.
  if (jaPostaram === total - 1) return "Você é o único que falta.";
  return `${jaPostaram} de ${total} já postaram.`;
}

/**
 * O que a notificação vai dizer. Mensagem própria vence a padrão.
 *
 * A frase do grupo é ACRESCENTADA, nunca substitui: quem escreveu a
 * própria mensagem escreveu o recado que queria ler, e trocá-lo por uma
 * estatística seria apagar uma escolha da pessoa.
 */
export function textoDoLembrete(
  slot: Lembrete, grupo?: GrupoNaRefeicao,
): { title: string; body: string } {
  const modelo = acharModelo(slot.id);
  const base = slot.texto || modelo?.corpo || "Registra o prato no Nhomi.";
  const frase = fraseDoGrupo(grupo);
  return {
    /* O emoji entra no TÍTULO, e só aqui: é o que separa, de relance na
       tela travada, um lembrete que se repete todo dia de um aviso de
       que alguém mexeu no seu prato. */
    title: `${modelo?.emoji ?? "🍽️"} ${modelo?.titulo ?? "Hora de registrar"}`,
    body: frase ? `${base} ${frase}` : base,
  };
}
